import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { distinctUntilChanged } from 'rxjs/operators';
import * as _ from 'lodash';
import to from 'await-to-js';
import { loadStripe } from '@stripe/stripe-js/pure';
import * as moment from 'moment';
import { InputControlService } from '../../../core/services/input-control.service';
import { ModalService } from '../../../core/services/modal.service';
import { UserService } from '../../../core/services/data/user.service';
import { OrderService } from '../../../core/services/data/order.service';
import { environment } from '../../../../environments/environment';
import { StripeService } from '../../../core/services/stripe.service';
import { IPopulatedOrder, IPopulatedGenres, IPopulatedStripePaymentMethod } from '../../../types/populated';
import { SettingsService } from '../../../core/services/data/settings.service';
import { GenreService } from '../../../core/services/data/genre.service';
import { CheckoutService } from '../../../core/services/checkout.service';
import { SavedPaymentsMethodsService } from '../../../core/services/data/saved-payment-methods.service';

import { IFeature, IUser, IPromocode } from '../../../types/datatypes';
import { StripeAddressOptions, StripePaymentIntentOptions } from '../../../types/local';
import {
	SettingAPIResponse,
	StripeCustomerReponse,
	StripePaymentIntentClientSecretResponse,
	StripePaymentMethodsResponse,
	StripePaymentIntentResponse,
	StripeErrorResponse,
	StripeAdressElementValuesResponse,
} from '../../../types/responses';
import { StripeFormFields } from './stripe-form-fields';

interface customWindow extends Window {
	development?: any;
}

declare const window: customWindow;
@Component({
	selector: 'app-stripe-form',
	templateUrl: './stripe-form.component.html',
	styleUrls: ['./stripe-form.component.scss'],
})
export class StripeFormComponent implements OnInit, OnDestroy {
	@Input() cartPurchaseEnabled;

	@Input() info;

	@Input() inProgress;

	@Input() feature: IFeature;

	@Input() promoCode: IPromocode;

	@Output() paymentSuccess = new EventEmitter();

	@Output() soldOutEvent = new EventEmitter();

	@Output() paymentStatus = new EventEmitter();

	public order: IPopulatedOrder;

	public orderSub;

	public amount;

	public errorText;

	public optedInToSaveCard;

	public errorTitle;

	public errorExtra;

	public paypalDisabled;

	public form;

	public formComponents = new StripeFormFields();

	public user: IUser;

	public soldOut = false;

	public buttonPaypal;

	public stripe;

	public elements;

	public stripeCustomer;

	public stripeCardElement;

	public stripeAddress;

	public stripePaymentError;

	public stripePaymentMethods = [];

	public stripePrimaryPaymentMethod = null;

	public stripeSelectedPaymentMethodId = '';

	public genres: IPopulatedGenres[];

	public savedPaymentMethods;

	public showCardForm = false;

	public addressOptions: StripeAddressOptions;

	public submitted = false;

	public checkoutSignupLogin = false;

	public errorOptions = {
		title: 'There was a problem',
		text: null,
		actions: [{
			type: 'solid',
			text: 'Got it',
			callback: () => this.modalService.close('payment-error-modal'),
		}],
	};

	private stripePaymentIntentClientSecret;

	private development;

	private STRIPE_META_DATA_KEY_LIMIT = 50;

	private source = 'Stripe';

	private paymentMethodsLoaded = false;

	private paymentIntentOptions: StripePaymentIntentOptions;

	constructor(
		private ics: InputControlService,
		private modalService: ModalService,
		private os: OrderService,
		private us: UserService,
		private ss: StripeService,
		private settingsService: SettingsService,
		private genreService: GenreService,
		private checkoutService: CheckoutService,
		private savedPaymentMethodsService: SavedPaymentsMethodsService,
	) {
		this.development = window.development;
	}

	paypalScriptLoaded() {
		const paypalScript = document.getElementById('paypal-script');
		return paypalScript !== null && paypalScript !== undefined;
	}

	ngOnInit() {
		if (this.info.email) {
			this.formComponents.email.value = this.info.email;
		}

		this.settingsService.getCheckoutSignupLoginSetting().then(
			(value) => {
				this.checkoutSignupLogin = !!value;
			},
		);

		this.form = InputControlService.toFormGroup(this.formComponents);
		this.us.user$.subscribe((data: any) => {
			if (data) {
				this.user = data;
				if (this.user?.username) {
					this.form.get('email').setValue(this.user.username);
					this.handleStripeCustomer();
				} else {
					this.form.get('email').setValue(this.info.email);
				}
			}
		});

		this.savedPaymentMethodsService.list().subscribe((data: any) => {
			if (data.success) {
				this.savedPaymentMethods = data.savedPaymentMethods;
			}
		});

		if (!this.paypalScriptLoaded()) {
			const clientId = this.development ? 'payPalSandboxClientId' : 'payPalProdClientId';
			const node = document.createElement('script');
			node.src = `https://www.paypal.com/sdk/js?client-id=${environment[clientId]}&disable-funding=credit,card`;
			node.type = 'text/javascript';
			node.id = 'paypal-script';
			document.getElementsByTagName('head')[0].appendChild(node);
		}

		this.settingsService.getPaypalButton().subscribe((data: SettingAPIResponse) => {
			if (data.success && data.setting) {
				// true == up
				this.buttonPaypal = data.setting.value;
			}
		});

		this.genreService.getGenres().subscribe((genres: IPopulatedGenres[]) => {
			this.genres = genres;
		});

		this.paypalDisabled = this.isSubscription(this.order);

		// On order change, re-check paypal
		this.orderSub = this.os.orderStore$
			.pipe(distinctUntilChanged((prev, curr) => _.isEqual(prev, curr)))
			.subscribe((order: IPopulatedOrder) => {
				this.order = order;
				this.paypalDisabled = this.isSubscription(order);
				this.amount = this.os.calculateOrderTotal();
				// REINIT IF ORDER CHANGES. DELAY FOR 100MS to allow order to circulate... Not the best.
				this.initStripeElements();
			});
	}

	ngOnDestroy(): void {
		this.orderSub?.unsubscribe();
	}

	isSubscription(order) {
		if (!order?.items) {
			return false;
		}
		const subscription = order.items.find((item) => item.itemType === 'subscription');

		return !!subscription;
	}

	handleSoldOut(event) {
		this.soldOutEvent.emit(event);
	}

	handlePaymentStatus(event) {
		this.paymentStatus.emit(event);
	}

	async onSubmit(event) {
		this.submitted = true;
		event.preventDefault();
		const thirtyDayError = this.os.checkOrderForThirtyDayViolation(this.order);
		const errors = this.ics.checkFormValid(this.form, this.formComponents);

		if (this.isSubscription(this.order) && !this.user) {
			this.handleError({ errorText: ['You must create an account or login to purchase a subscription.'] });
			return;
		}

		if (thirtyDayError && thirtyDayError.errorText) {
			this.handleError({ errorText: [thirtyDayError.errorText] });
			return;
		}
		if (errors.hasErrors) {
			this.handleError({ errorText: errors.errorText });
			return;
		}

		this.os.checkOrderSiteInventory(this.order._id).subscribe(async (invData: any) => {
			if (invData.success && invData.invalidFeatures && invData.advertsWithInventoryConflict) {
				if (!invData.invalidPromoStacks.length && !invData.invalidFeatures.length
					&& !invData.advertsWithInventoryConflict.length) {
					let paymentIntent;
					if (this.stripeSelectedPaymentMethodId) {
						paymentIntent = await this.handlePaymentWithStoredCard();
					} else {
						paymentIntent = await this.handlePaymentElement();
					}

					await this.updateUserShippingAddress();

					if (!paymentIntent) {
						return this.handleError({ errorText: [`${this.stripePaymentError}`] });
					}

					const data: any = this.form.value;
					data.paymentIntent = paymentIntent;

					const _event = { type: 'stripe', status: 'success', data };

					const response = this.checkoutService.handleSuccess(this.order, 'Stripe', this.amount);
					this.soldOut = response.soldOut;
					return this.paymentSuccess.emit(_event);
				}

				if (!invData.invalidPromoStacks.length && invData.advertsWithInventoryConflict.length) {
					const response = this.checkoutService.handleAdvertInventoryConflict(invData, this.order, 'Stripe');
					return this.handleError(response);
				}

				if (invData.invalidFeatures.length) {
					const response = this.checkoutService.handleSoldOutCart({ data: invData.invalidFeatures }, this.order, 'Stripe');
					return this.handleError(response);
				}

				if (invData.invalidPromoStacks.length) {
					const invalidPromoStacks = this.order.items.filter(({ itemId }) => invData.invalidPromoStacks
						.find(({ orderPromoStackId }) => orderPromoStackId === itemId));

					this.errorText = [`The promo stacks you selected are no longer available for the dates you selected.
						Please select a new date for your promo stack.`];

					this.errorExtra = invalidPromoStacks.reduce((acc, { itemId, item: { _promoStack, startDate } }) => {
						const dateLabel = _promoStack.releaseDuration > 1 ? 'starting' : '';

						acc[itemId] = `${_promoStack.name} ${dateLabel} on ${moment(startDate, 'YYYYMMDD').format('MMMM Do, YYYY')}`;
						return acc;
					}, {});

					this.modalService.open('payment-error-modal');
					this.soldOut = true;
					return null;
				}

				return this.handleError({
					errorText: ['The date you selected is no longer available'],
					soldOut: true,
				});
			}

			return this.handleError({
				errorText: ['An error occurred, please try again.'],
				soldOut: true,
			});
		});
	}

	handlePayPalResult(event) {
		if (event.status === 'success') {
			const { soldOut } = this.checkoutService.handleSuccess(this.order, 'PayPal', this.amount);
			this.soldOut = soldOut;
			this.paymentSuccess.emit(event);
			return;
		}

		if (event.status === 'cancelled') {
			const response = this.checkoutService.handleCancelled(this.order, 'PayPal');
			this.handleError(response);
			this.paymentStatus.emit({ loading: false });
			return;
		}

		if (event.type === 'sold_out_cart') {
			const response = this.checkoutService.handleSoldOutCart(event, this.order, 'PayPal');
			this.handleError(response);
			return;
		}

		if (event.type === 'advert_inventory_conflict') {
			const response = this.checkoutService.handleAdvertInventoryConflict(event, this.order, 'PayPal');
			this.handleError(response);
			return;
		}

		if (event.type === 'sold_out_promo_stack') {
			this.checkoutService.handleAdvertInventoryConflict(event, this.order, 'PayPal');
			this.handleError(event);
			return;
		}

		if (event.type === 'sold_out') {
			const response = this.checkoutService.handleSoldOut(this.order, 'PayPal');
			this.handleError(response);
			return;
		}

		const message = this.checkoutService.parseUnknownPaypalError(event.error);
		this.handleError({ errorText: [`There was an error processing your PayPal transaction: ${message}`] });
	}

	handleError({ errorText, errorExtra = null, errorTitle = '', soldOut = false }) {
		this.errorText = errorText;
		this.errorTitle = errorTitle;
		this.errorExtra = errorExtra;
		this.soldOut = soldOut;
		this.submitted = false;

		this.errorOptions.text = errorText;
		if (soldOut) {
			this.errorOptions.actions = [
				{
					type: 'solid',
					text: 'Pick Another Date',
					callback: () => {
						const invalidFeatureMetaIds = Object.keys(errorExtra);

						this.modalService.close('payment-error-modal');
						this.handleSoldOut(invalidFeatureMetaIds[0]);
					},
				},
			];
		}

		this.modalService.open('payment-error-modal');
	}

	async initStripeElements() {
		await this._intiializeStripe();

		const itemCount = this.order.items.length;
		const description = `Purchase of ${itemCount} ${itemCount > 1 ? 'items' : 'item'}. OrderId: ${this.order._id}`;
		const completedOrderObj = {
			metadata: {
				order_id: this.order._id,
			},
		};

		this.order.items.map(({ itemId }, index) => {
			const indexItem = index + 1;

			if (indexItem < this.STRIPE_META_DATA_KEY_LIMIT) {
				completedOrderObj.metadata = {
					...completedOrderObj.metadata,
					...{ [`item_${indexItem}_ID`]: itemId },
				};
			}

			return null;
		});

		// REMOVE SUBSCRIPTION PRICE AS THAT WILL BE PAID FOR BY THE SUBSCRIPTION ITSELF.
		// UNFORTUNATELY THERE SEEMS TO BE NO WAY TO APPLY THE SUBSCRIPTION COST TO AN EXISTING INVOICE.
		const subscription = this.order.items.find(({ itemType }) => itemType === 'subscription');
		let adjustedAmount = this.amount;
		if (subscription) {
			adjustedAmount -= subscription.item.paymentAmount;
		}

		await this._createUpdatePaymentIntent(adjustedAmount, completedOrderObj, description);
	}

	async _intiializeStripe() {
		if (this.stripe) return;
		const stripeKey = this.development ? 'stripePKTest' : 'stripePKProd';
		this.stripe = await loadStripe(environment[stripeKey]);
	}

	useNewCard() {
		this.showCardForm = true;
		this.stripeSelectedPaymentMethodId = null;
	}

	populateAddressDefaultValues() {
		const { user } = this;
		const { shippingInfo } = user;
		const defaultValues = {
			name: `${user.firstName} ${user.lastName}`,
			address: {
				city: this.isset(shippingInfo?.city),
				country: this.isset(shippingInfo?.country),
				line1: this.isset(shippingInfo?.address?.street1),
				line2: this.isset(shippingInfo?.address?.street2),
				postal_code: this.isset(shippingInfo?.postalCode?.toString()),
				state: this.isset(shippingInfo?.state),
			},
		};

		return defaultValues;
	}

	isset(value) { return value || ''; }

	handleStripeCustomer() {
		if (this.user.stripeCustomerId) {
			return this.getStripeCustomer();
		}

		return this.checkStripeCustomer();
	}

	getClientSecret(orderId) {
		return new Promise((resolve) => {
			this.ss.getPaymentIntentClientSecret(this.paymentIntentOptions, orderId).subscribe((result: StripePaymentIntentClientSecretResponse) => {
				if (!result.success) return resolve(null);

				return resolve(result.clientSecret);
			});
		});
	}

	checkStripeCustomer(): Promise<string> {
		return new Promise((resolve) => {
			this.ss.createStripeCustomer(this.user.username).subscribe((result: StripeCustomerReponse) => {
				if (!result.success) return resolve(null);

				this.user.stripeCustomerId = result.customer.id;
				this.stripeCustomer = result.customer;

				const fieldsToUpdate = {
					stripeCustomerId: result.customer.id,
				};

				this.us.saveProfile(this.user._id, fieldsToUpdate).subscribe();
				this.us.updateUser(this.user);

				return resolve(result.customer.id);
			});
		});
	}

	getStripeCustomer(): Promise<string> {
		return new Promise((resolve) => {
			this.ss.getCustomer(this.user.stripeCustomerId).subscribe((result: StripeCustomerReponse) => {
				if (!result.success) return resolve(null);

				this.stripeCustomer = result.customer;
				return resolve(result.customer.id);
			});
		});
	}

	async updateUserShippingAddress() {
		if (!this.user) return;

		const [error, resultValues]: [StripeErrorResponse, StripeAdressElementValuesResponse] = await to(this.stripeAddress.getValue());
		if (error) {
			throw error;
		}

		const billing = resultValues?.value || null;
		const { address } = billing;
		const { line1, line2, city, country, state, postal_code: postalCode } = address;

		let fieldsToUpdate = {
			shippingInfo: {
				address: {
					street1: line1,
					street2: line2,
				},
				city,
				country,
				state,
				postalCode,
			},
		};

		if (!fieldsToUpdate.shippingInfo.address.street1) {
			fieldsToUpdate = _.omit(fieldsToUpdate, 'shippingInfo.address.street1');
		}
		if (!fieldsToUpdate.shippingInfo.address.street2) {
			fieldsToUpdate = _.omit(fieldsToUpdate, 'shippingInfo.address.street2');
		}
		if (Object.keys(fieldsToUpdate.shippingInfo.address).length === 0) {
			fieldsToUpdate = _.omit(fieldsToUpdate, 'shippingInfo.address');
		}
		if (!fieldsToUpdate.shippingInfo.city) {
			fieldsToUpdate = _.omit(fieldsToUpdate, 'shippingInfo.city');
		}
		if (!fieldsToUpdate.shippingInfo.country) {
			fieldsToUpdate = _.omit(fieldsToUpdate, 'shippingInfo.country');
		}
		if (!fieldsToUpdate.shippingInfo.state) {
			fieldsToUpdate = _.omit(fieldsToUpdate, 'shippingInfo.state');
		}
		if (!fieldsToUpdate.shippingInfo.postalCode) {
			fieldsToUpdate = _.omit(fieldsToUpdate, 'shippingInfo.postalCode');
		}

		this.user.shippingInfo = fieldsToUpdate.shippingInfo;

		this.us.saveProfile(this.user._id, fieldsToUpdate).subscribe();
		this.us.updateUser(this.user);
		this.us.updateAuthor(this.user);
		this.updateStripeUser();
	}

	async updateStripeUser() {
		const [error, resultValues]: [StripeErrorResponse, StripeAdressElementValuesResponse] = await to(this.stripeAddress.getValue());
		if (error) {
			throw error;
		}

		const { value } = resultValues;
		const addressElement = value || null;

		if (!addressElement) return;

		const { address, name } = addressElement;
		const fieldsToUpdate = {
			name,
			address,
		};

		this.ss.updateCustomerInfo(this.user.stripeCustomerId, fieldsToUpdate).subscribe();
	}

	async handlePaymentElement() {
		const { stripeCardElement: card } = this;
		const resultValues: StripeAdressElementValuesResponse = await this.stripeAddress.getValue();

		if (!resultValues.complete) throw new Error('Missing Billing Information');

		const { value } = resultValues;
		const billing = value || null;

		const [error, result]: [StripeErrorResponse, StripePaymentIntentResponse] = await to(
			this.stripe.confirmCardPayment(this.stripePaymentIntentClientSecret, {
				payment_method: {
					card,
					billing_details: {
						address: billing?.address,
						name: billing?.name,
					},
				},
			}),
		);

		if (error || result.error) {
			this._handleConfirmPaymentError(error, result);

			return null;
		}

		if (result.paymentIntent.payment_method && this.optedInToSaveCard) {
			const { payment_method: paymentMethod } = result.paymentIntent;
			const paymentMethodId = typeof paymentMethod === 'string' ? paymentMethod : paymentMethod.id;
			this.savedPaymentMethodsService.create({ _user: this.user._id, paymentMethodId }).subscribe();
		}

		return result.paymentIntent;
	}

	async handlePaymentWithStoredCard() {
		const resultValues: StripeAdressElementValuesResponse = await this.stripeAddress.getValue();

		if (!resultValues.complete) throw new Error('Missing Billing Information');

		const billing = resultValues?.value || null;

		const [error, result]: [StripeErrorResponse, StripePaymentIntentResponse] = await to(
			this.stripe.confirmCardPayment(this.stripePaymentIntentClientSecret, {
				payment_method: this.stripeSelectedPaymentMethodId,
				shipping: {
					address: billing?.address,
					name: billing?.name,
				},
			}),
		);

		if (error || result.error) {
			this._handleConfirmPaymentError(error, result);

			return null;
		}

		return result.paymentIntent;
	}

	_handleConfirmPaymentError(error, result) {
		this.stripePaymentError = 'There was an error trying to process the payment.';

		if (result?.error?.message) {
			this.stripePaymentError = JSON.stringify(result.error.message);
		}
	}

	getStripePaymentMethods() {
		// If payments are already loaded, if there are no saved payments, or if the user has not opted into saved payments, return.

		if (this.paymentMethodsLoaded) {
			return;
		}

		if (!this.savedPaymentMethods?.length || this.savedPaymentMethods.length === 0) {
			return;
		}

		this.paymentMethodsLoaded = true;

		this.ss.getCustomerPaymentMethods(this.user.stripeCustomerId).subscribe((result: StripePaymentMethodsResponse) => {
			if (!result.success || !result.paymentMethods) {
				this.stripePaymentError = 'Unable to resolve payment methods';
				return;
			}

			const savedPaymentMethodIds = this.savedPaymentMethods.map((paymentMethod) => paymentMethod.paymentMethodId);
			const paymentMethods = result.paymentMethods.filter((item) => savedPaymentMethodIds.includes(item.id));

			const [primary] = paymentMethods;
			this.stripePrimaryPaymentMethod = primary;

			if (primary) {
				this.stripePaymentMethods = paymentMethods.filter(
					(paymentMethod: IPopulatedStripePaymentMethod) => paymentMethod.id !== primary.id,
				);
			} else {
				this.stripePaymentMethods = paymentMethods;
			}
		});
	}

	selectedStripeCard(paymentMethodId) {
		this.stripeSelectedPaymentMethodId = paymentMethodId;

		// Update payment Intent
		this._updatePaymentIntentPaymentMethod(paymentMethodId);
	}

	setValue(event, target) {
		if (!this.form.controls[target]) return;

		this.form.controls[target].patchValue(event.target.value);
	}

	async generateNewPaymentIntent(adjustedAmount, completedOrderObj, description) {
		this.addressOptions = {
			mode: 'billing',
			defaultValues: {
				name: '',
				address: {
					city: '',
					country: '',
					line1: '',
					line2: '',
					postal_code: '',
					state: '',
				},
			},
		};

		let customer;

		if (this.user) {
			customer = await this.handleStripeCustomer();
			this.getStripePaymentMethods();

			this.addressOptions.defaultValues = this.populateAddressDefaultValues();
		}

		this.paymentIntentOptions = this.ss.createPaymentIntentOptions(adjustedAmount, completedOrderObj.metadata, description, customer);
	}

	async _createUpdatePaymentIntent(adjustedAmount, completedOrderObj, description) {
		if (this.submitted) {
			return;
		}

		if (!this.stripePaymentIntentClientSecret) {
			// Update existing Payment intent
			await this.generateNewPaymentIntent(adjustedAmount, completedOrderObj, description);
			await this._generateStripeForm();
			return;
		}

		const chargeAmount = Math.round(adjustedAmount * 100);

		const { amount, customer } = this.paymentIntentOptions;

		const paymentIntentId = this.ss.getPaymentIntentFromToken(this.stripePaymentIntentClientSecret);

		if (chargeAmount !== amount) {
			// Create new Intent
			this.ss.updatePaymentIntentAmount(paymentIntentId, chargeAmount).subscribe(
				(result) => this.updatePaymentIntentAmount(result, chargeAmount),
			);
		}

		if (customer !== this.user?.stripeCustomerId) {
			this.ss.updatePaymentIntentCustomer(paymentIntentId, this.user?.stripeCustomerId).subscribe();
		}
	}

	// The payment was updated on the backend successfully, so we update the local state.
	updatePaymentIntentAmount(result, updatedAmount) {
		if (result.success) {
			this.paymentIntentOptions.amount = updatedAmount;
		}
	}

	async _updatePaymentIntentPaymentMethod(paymentMethodId) {
		const paymentIntentId = this.ss.getPaymentIntentFromToken(this.stripePaymentIntentClientSecret);

		if (!paymentIntentId) {
			return;
		}

		this.ss.updatePaymentIntentPaymentMethod(paymentIntentId, paymentMethodId).subscribe();
	}

	async _generateStripeForm() {
		if (this.stripePaymentIntentClientSecret) {
			return;
		}
		const appearance = {
			theme: 'flat',
			variables: { colorPrimaryText: '#262626' },
		};

		const cardOptions = {
			style: {
				base: {
					fontSize: '16px',
				},
			},
			hidePostalCode: true,
		};
		this.stripePaymentIntentClientSecret = await this.getClientSecret(this.order._id);
		this.elements = this.stripe.elements({ clientSecret: this.stripePaymentIntentClientSecret, appearance });
		this.stripeCardElement = this.elements.create('card', cardOptions);
		this.stripeCardElement.mount('#stripe-card');
		this.stripeAddress = this.elements.create('address', this.addressOptions);
		this.stripeAddress.mount('#stripe-address');
	}
}
